import { auth } from './firebase';
import { getApiBaseUrl } from '../utils/api';

const API_BASE_URL = getApiBaseUrl();

export interface IPHistoryEntry {
  id: string;
  ipAddress: string;
  eventType: 'login' | 'register';
  timestamp: any;
  createdAt: string;
}

export interface UserIPInfo {
  email: string;
  lastLoginIP: string;
  lastLoginAt: any;
  firstLoginIP: string;
  firstLoginAt: any;
  totalLogins: number;
}

class IPTrackingService {
  /**
   * Record IP address for the current user
   */
  async recordIP(eventType: 'login' | 'register'): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/recordIP`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ eventType }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to record IP address');
      }

      const data = await response.json();
      console.log('IP address recorded:', data.message);
    } catch (error) {
      console.error('Error recording IP address:', error);
      // Don't throw error - IP tracking should not block user flow
    }
  }

  /**
   * Get current user's IP history
   */
  async getMyIPHistory(limit: number = 10): Promise<IPHistoryEntry[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getMyIPHistory?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get IP history');
      }

      const data = await response.json();
      return data.history || [];
    } catch (error) {
      console.error('Error getting IP history:', error);
      throw error;
    }
  }

  /**
   * Get IP history for a specific user (admin only)
   */
  async getUserIPHistory(email: string, limit: number = 10): Promise<{ userInfo: any; history: IPHistoryEntry[] }> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getUserIPHistory?email=${encodeURIComponent(email)}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get user IP history');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting user IP history:', error);
      throw error;
    }
  }

  /**
   * Get all users with their IP information (admin only)
   */
  async getAllUsersIPInfo(): Promise<UserIPInfo[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getAllUsersIPInfo`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get all users IP info');
      }

      const data = await response.json();
      return data.users || [];
    } catch (error) {
      console.error('Error getting all users IP info:', error);
      throw error;
    }
  }
}

export const ipTrackingService = new IPTrackingService();
